import React, { useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { Add } from '@mui/icons-material'
import { Box, TableContainer } from '@mui/material'

import SectionTitle from '../../../../components/form/SectionTitle'
import Datatable, { DatatableHeaderProps } from '../../../../components/layout/Datatable'
import { FormDialog } from '../../../../components/layout/FormDialog'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { RedDeleteIcon } from '../../../../components/form/styled'
import { EditionModeContextProvider } from '../../../../utils/providers/editionModeContext'
import { CampaignCreateAdgroupDraft } from './campaignCreateDrafts'
import { CampaignCreateAdgroupModal } from './CampaignCreateAdgroupModal'
import { useCampaignCreateCopperContext } from './CampaignCreateCopperContext'
import { getCampaignCreateAdgroupBudgetLines } from './campaignCreateAdgroupBudget.utils'

type AdgroupListItem = {
  id: string
  name: string
  format: string
  budget: Array<{ label: string; amount: string; type: string }>
}

export const CampaignCreateAdgroupsSection: React.FC = () => {
  const t = useAppTranslations()
  const { values, setFieldValue } = useFormikContext<{ adgroups: CampaignCreateAdgroupDraft[] }>()
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const { opportunityBudgetRemaining } = useCampaignCreateCopperContext()

  const adgroups = values.adgroups ?? []

  const tableData: AdgroupListItem[] = useMemo(
    () =>
      adgroups.map((adgroup) =>
        Object.assign(
          {},
          {
            id: adgroup.draftId,
            name: adgroup.name,
            format: adgroup.format ?? '',
            budget: getCampaignCreateAdgroupBudgetLines(adgroup, t),
          }
        )
      ),
    [adgroups, t]
  )

  const tableHeaders: DatatableHeaderProps<keyof AdgroupListItem>[] = useMemo(
    () => [
      { name: 'name' },
      { name: 'format' },
      {
        name: 'budget',
        renderFn: (item: AdgroupListItem) => (
          <Box component="ul" role="list" sx={{ m: 0, p: 0, listStyle: 'none', minWidth: 240 }}>
            {item.budget.map((line) => (
              <Box
                component="li"
                key={line.label}
                role="listitem"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 2,
                  py: 0.25,
                }}
              >
                <Box>{line.label}</Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                  <Box>{line.amount}</Box>
                  <Box>{line.type}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        ),
      },
    ],
    []
  )

  const datatableButtons = useMemo(
    () => [
      {
        type: () => <RedDeleteIcon />,
        action: (item: AdgroupListItem) => {
          setFieldValue(
            'adgroups',
            adgroups.filter((adgroup) => adgroup.draftId !== item.id)
          )
        },
        label: t('commons.actions.delete'),
      },
    ],
    [adgroups, t]
  )

  const actionButtons = useMemo(
    () => [
      {
        startIcon: <Add />,
        label: 'commons.actions.add',
        testingId: 'addCampaignCreateAdgroup',
        onClick: () => setOpenCreateModal(true),
      },
    ],
    []
  )

  return (
    <EditionModeContextProvider>
      <SectionTitle
        withFormik={false}
        title="commons.adgroups"
        helperText={t('campaign.adgroupsHelper')}
        actionButtons={actionButtons}
      />
      <TableContainer>
        {tableData.length > 0 ? (
          <Datatable
            headers={tableHeaders}
            data={tableData}
            actionsButtons={datatableButtons}
            translateKey="commons."
            orderByDefault="name"
          />
        ) : null}
      </TableContainer>

      <FormDialog title={t('adgroup.create')} open={openCreateModal} setOpen={setOpenCreateModal}>
        <CampaignCreateAdgroupModal
          setOpen={setOpenCreateModal}
          opportunityBudgetRemaining={opportunityBudgetRemaining}
          onAdd={(adgroup) => setFieldValue('adgroups', [...adgroups, adgroup])}
        />
      </FormDialog>
    </EditionModeContextProvider>
  )
}
